/**
 * The dashboard's AI-editing API (backend metaplan A10; contract `../apiv2/dashboard_ai_edit.openapi.yaml`,
 * mounted under `/v2/edit/`).
 *
 * Every call passes `keepSessionOn403: true`: the data provider signs the session out on any 403 otherwise,
 * and here a 403 only means "you lack an explicit notification.mcp_* grant (with all tags, or for every scope a
 * pending change touches)", "you lack ai_edit.manage" or "you lack a vocabulary.mcp_* grant" — the pages check
 * `GET ai_changes/permissions/` (or `hasExplicitPerm`) first, but a grant revoked mid-session must not log the user out.
 *
 * GETs go through `GET_ONE` (it strips `keepSessionOn403` from the query string; `GET_LIST` would not) and
 * POSTs through `CREATE` on a sub-path; both are wrapped by `withFranchiseContext`, which adds the selected
 * licensee as `franchise_to` to GETs (and an ignored `tour_operator` key to POST bodies; the AI vocabulary's POST,
 * which refuses that key, opts out with `explicitTenant`).
 *
 * Each function resolves to the response body and rejects with an {@link AIEditApiError} for any status
 * ≥ 400 (`status`, `code` from the body when it has one, `body`).
 */
import { CREATE, DELETE, GET_ONE, UPDATE_BATCH } from '../../api/dataProvider';

export const KEEP_SESSION = { keepSessionOn403: true };

export class AIEditApiError extends Error {
    constructor(status, body) {
        const detail = (body && (body.detail || body.message)) || `Request failed (${status})`;
        super(typeof detail === 'string' ? detail : JSON.stringify(detail));
        this.status = status;
        this.body = body;
        this.code = (body && typeof body.code === 'string' && body.code) || null;
    }
}

const unwrap = (res) => {
    const status = res?.statusCode ?? 0;
    if (status >= 400 || status === 0) {
        throw new AIEditApiError(status, res?.data && !Array.isArray(res.data) ? res.data : null);
    }
    return res.data;
};

/** Drop empty filter values so they never reach the query string. */
const clean = (filter) => {
    const out = {};
    Object.entries(filter || {}).forEach(([k, v]) => {
        if (v === undefined || v === null || v === '') return;
        out[k] = Array.isArray(v) ? v.join(',') : v;
    });
    return out;
};

const get = (dataAPI, resource, { id, urlSuffix, filter } = {}) => {
    const params = { ...KEEP_SESSION };
    if (id !== undefined && id !== null) params.id = id;
    if (urlSuffix) params.urlSuffix = urlSuffix;
    const f = clean(filter);
    if (Object.keys(f).length) params.filter = f;
    return dataAPI(GET_ONE, resource, params).then(unwrap);
};

const post = (dataAPI, resource, data) => dataAPI(CREATE, resource, { ...KEEP_SESSION, data: data || {} }).then(unwrap);

// ---- AI changes -------------------------------------------------------------------------------------------

export const getPermissions = (dataAPI) => get(dataAPI, 'ai_changes/permissions');

/** The change sets (`batchId`: one content apply's, one per record -- client-MCP plan §4.7 "Batches"). */
export const listChangeSets = (dataAPI, { page = 1, pageSize = 25, batchId, ...filter } = {}) =>
    get(dataAPI, 'ai_changes', { filter: { page, page_size: pageSize, ...filter, batch_id: batchId } });

export const getChangeSet = (dataAPI, id) => get(dataAPI, 'ai_changes', { id });

export const listEntries = (dataAPI, id) => get(dataAPI, 'ai_changes', { id, urlSuffix: 'entries/' });

export const getEntryDiff = (dataAPI, id, seq) => get(dataAPI, 'ai_changes', { id, urlSuffix: `entries/${seq}/` });

export const getHistory = (dataAPI, id) => get(dataAPI, 'ai_changes', { id, urlSuffix: 'history/' });

export const getRevertPlan = (dataAPI, id, { skips = [], confirmSoftRefs = false } = {}) =>
    get(dataAPI, 'ai_changes', {
        id,
        urlSuffix: 'revert_plan/',
        filter: { skips: skips.length ? skips.join(',') : undefined, confirm_soft_refs: confirmSoftRefs ? 'true' : undefined },
    });

export const startRevert = (dataAPI, id, { reason, expectedDigest, skips = [], confirmSoftRefs = false }) =>
    post(dataAPI, `ai_changes/${id}/revert`, {
        reason,
        expected_digest: expectedDigest,
        skips,
        confirm_soft_refs: !!confirmSoftRefs,
    });

export const getRevertOutcome = (dataAPI, id, taskId) => get(dataAPI, 'ai_changes', { id, urlSuffix: `revert/${taskId}/` });

export const listPending = (dataAPI, filter = {}) => get(dataAPI, 'ai_changes/pending', { filter });

export const getPending = (dataAPI, id) => get(dataAPI, 'ai_changes/pending', { id });

export const applyPending = (dataAPI, id, { note, expectedDigest } = {}) =>
    post(dataAPI, `ai_changes/pending/${id}/apply`, {
        ...(note ? { note } : {}),
        ...(expectedDigest ? { expected_digest: expectedDigest } : {}),
    });

export const rejectPending = (dataAPI, id, { note } = {}) =>
    post(dataAPI, `ai_changes/pending/${id}/reject`, note ? { note } : {});

export const getProposal = (dataAPI, id) => get(dataAPI, 'ai_changes/proposals', { id });

export const getProposalRenders = (dataAPI, id) => get(dataAPI, 'ai_changes/proposals', { id, urlSuffix: 'renders/' });

// ---- AI editing settings (`/tour_operator/ai_edit/`, explicit ai_edit.manage): MCP auto-apply (client-MCP plan
// Q26) and the guest-data opt-in (§6.4, §13.2, Q8; backend P1b-06) ------------------------------------------------

/** The operators the viewer manages, each with its switches and the ops lock (`DashboardAIEditSettingsList`). */
export const getAIEditSettings = (dataAPI) => get(dataAPI, 'tour_operator/ai_edit');

/**
 * Change one operator's settings (`PUT …?franchise_to=<id>`; the answer's `changed` says whether anything changed):
 * `switches` holds only the switch being turned, so the other one is never re-sent (the ops lock of auto-apply refuses
 * nothing else). The data provider's PUT without an id is UPDATE_BATCH; `reason` (optional) goes to the configuration
 * history.
 */
const putAIEditSettings = (dataAPI, tourOperatorId, switches, reason) => {
    const note = (reason || '').trim();
    return dataAPI(UPDATE_BATCH, 'tour_operator/ai_edit', {
        ...KEEP_SESSION,
        urlSuffix: `?franchise_to=${encodeURIComponent(tourOperatorId)}`,
        data: { ...switches, ...(note ? { reason: note } : {}) },
    }).then(unwrap);
};

/** Turn one operator's MCP auto-apply on or off. */
export const setAutoApply = (dataAPI, tourOperatorId, { autoApply, reason } = {}) =>
    putAIEditSettings(dataAPI, tourOperatorId, { mcp_auto_apply: !!autoApply }, reason);

/**
 * Turn one operator's guest-data opt-in (`mcp_pii_tools`) on or off. On carries the admin's acknowledgment that the
 * company's agreement with its AI provider covers guest data (`acknowledged_provider_agreement`, the dialog's
 * checkbox): the backend refuses on without it (400 `provider_agreement_not_acknowledged`). Off sends nothing extra.
 */
export const setPiiTools = (dataAPI, tourOperatorId, { piiTools, acknowledgedProviderAgreement, reason } = {}) =>
    putAIEditSettings(dataAPI, tourOperatorId, piiTools
        ? { mcp_pii_tools: true, acknowledged_provider_agreement: !!acknowledgedProviderAgreement }
        : { mcp_pii_tools: false }, reason);

// ---- the AI vocabulary (`/ai_vocabulary/`, client-MCP plan §12.5, Q15; backend P2-09) -------------------------
// Explicit vocabulary.mcp_write (propose) / vocabulary.mcp_approve (read the page; approve in the inbox).

/**
 * One operator's vocabulary (`DashboardAIVocabulary`): the sidebar's licensee (`franchise_to`), else the viewer's own --
 * or `tourOperatorId`, named in the URL whatever the sidebar shows (the page opened from an inbox request).
 */
export const getVocabulary = (dataAPI, { tourOperatorId } = {}) => (tourOperatorId === undefined || tourOperatorId === null
    ? get(dataAPI, 'ai_vocabulary')
    : dataAPI(GET_ONE, 'ai_vocabulary', {
        ...KEEP_SESSION,
        explicitTenant: true,
        urlSuffix: `?franchise_to=${encodeURIComponent(tourOperatorId)}`,
    }).then(unwrap));

/**
 * Propose an edit of one operator's vocabulary (`DashboardAIVocabularyEdit`: `create` with `fields`, `update` with
 * `entry` and the fields that change, `delete` with `entry`) and a reason. Resolves with the
 * `DashboardAIVocabularyEditResult` -- `applied` (200) or waiting for an approver (202). The operator goes in the URL
 * (`franchise_to`, the one the page shows) and the body holds only the contract's keys: `explicitTenant` keeps the
 * franchise wrapper from adding `tour_operator`, which the backend refuses.
 */
export const proposeVocabularyEdit = (dataAPI, tourOperatorId, { action, entry, fields, reason }) => dataAPI(CREATE, 'ai_vocabulary', {
    ...KEEP_SESSION,
    explicitTenant: true,
    urlSuffix: `?franchise_to=${encodeURIComponent(tourOperatorId)}`,
    data: {
        action,
        ...(entry !== undefined && entry !== null ? { entry } : {}),
        ...(fields ? { fields } : {}),
        reason: (reason || '').trim(),
    },
}).then(unwrap);

// ---- read-only notification content -----------------------------------------------------------------------

/** `resource`: notification_block | block_list | notification_theme | language_policy | language_alias. */
export const listContent = (dataAPI, resource, { page = 1, pageSize = 25, query, system, type } = {}) =>
    get(dataAPI, resource, { filter: { page, page_size: pageSize, query, system, type } });

export const getContent = (dataAPI, resource, id) => get(dataAPI, resource, { id });

// ---- tags ---------------------------------------------------------------------------------------------------

export const getEffectiveSchema = (dataAPI, categoryId) =>
    get(dataAPI, 'tour_tag_category', { id: categoryId, urlSuffix: 'effective_schema/' });

export const listTagCategories = (dataAPI, { page = 1, pageSize = 1000 } = {}) =>
    get(dataAPI, 'tour_tag_category', { filter: { page, page_size: pageSize, ordering: 'name' } });

// ---- MCP connections (contract ../apiv2/authserver.openapi.yaml, /v2/edit/oauth/connections/) -------------

export const listConnections = (dataAPI) => get(dataAPI, 'oauth/connections');

export const revokeConnection = (dataAPI, grantId) =>
    dataAPI(DELETE, 'oauth/connections', { ...KEEP_SESSION, id: encodeURIComponent(grantId) }).then((res) => {
        const status = res?.statusCode ?? 0;
        if (status >= 400 || status === 0) {
            throw new AIEditApiError(status, res?.data && !Array.isArray(res.data) ? res.data : null);
        }
        return null;
    });

/** The M9 envelope's `code` (`connection_not_found`, `authserver_disabled`, …), else null. */
export const errorCode = (err) => (err instanceof AIEditApiError ? err.code : null);
